import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { ProductVisibilityType } from "@omni/types/api/product/bff/shared/enums/productVisibilityType.enum";
import { cn } from "@omni/utils/styles";
import { ReactNode, useEffect, useRef, useState } from "react";

import { getDeliveryType } from "@/redux/modules/value/productDetail/productDetail.module";
import { getTagZoneIdFor } from "@/utils/tagging/tag.utils";

import { useECommerceContext } from "../../context/eCommerce.context";
import { useStorePickerModalContext } from "../../context/storePickerModal.context";
import { useECommerceProduct } from "../../hooks/useECommerceProduct.hook";
import { useProductDetail } from "../../hooks/useProductDetail.hook";
import { DeliveryContextProvider } from "./context/delivery.context";

type Props = {
    className?: string;
    children: ReactNode;
};

const DeliveryBase = ({ className, children }: Props) => {
    const {
        sku,
        visibilityType,
        hasDelivery,
        minimumOrderQuantity,
        roundingValue,
        isLimitedStock,
        isBoxArticle,
        isBoxSoldOut,
        deliveryTypes,
        deliveryOptions,
        montageParents,
        montages,
        boxLogisticInformation
    } = useECommerceProduct();

    const { setStorePickerModalState } = useStorePickerModalContext();

    const { updateProductDetail, deliveryType, quantity, serviceProductSkus } =
        useProductDetail();

    const { xfPath, tooltips, infoLink, isEcommerceReady, setInitialised } =
        useECommerceContext();

    const [deliveryTypesDisabled, setDeliveryTypesDisabled] = useState(true);

    const deliveryRef = useRef<HTMLDivElement>(null);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        const deliveryTypeToSet = getDeliveryType();
        let quantityToSet = Math.max(quantity || 0, 1);

        if (deliveryTypeToSet !== DeliveryType.ClickAndCollect) {
            quantityToSet = minimumOrderQuantity;
        }

        updateProductDetail({
            deliveryType: deliveryTypeToSet,
            quantity: quantityToSet,
            sku: sku
        }).then(() => {
            setDeliveryTypesDisabled(false);
            setInitialised(true);
        });
    }, []);

    useEffect(() => {
        if (deliveryType !== DeliveryType.HomeDelivery) return;

        const currentQuantity = quantity || 0;
        const newQuantity = Math.max(currentQuantity, minimumOrderQuantity);

        if (currentQuantity !== newQuantity) {
            updateProductDetail({ quantity: newQuantity });
        }
    }, [deliveryType, minimumOrderQuantity, quantity, updateProductDetail]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (!deliveryRef.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                handleOnEcommerceBlockVisibleChange(entry.isIntersecting);
            },
            {
                root: null, // viewport
                threshold: 0
            }
        );

        observer.observe(deliveryRef.current);

        return () => observer.disconnect();
    }, [deliveryRef]);

    const onlyAvailableInStore =
        visibilityType === ProductVisibilityType.OnlyAvailableInStore;

    const handleOnEcommerceBlockVisibleChange = (visible: boolean) => {
        const stickyOrderButton =
            !visible && hasDelivery && !onlyAvailableInStore;

        updateProductDetail({ stickyOrderButton: stickyOrderButton });
    };

    return (
        <DeliveryContextProvider
            sku={sku}
            onProductDetailChange={updateProductDetail}
            quantity={quantity}
            serviceProductSkus={serviceProductSkus}
            deliveryType={deliveryType}
            visibilityType={visibilityType}
            deliveryOptions={deliveryOptions}
            minimumOrderQuantity={minimumOrderQuantity}
            roundingValue={roundingValue}
            xfPath={xfPath}
            tooltips={tooltips}
            isLimitedStock={isLimitedStock}
            infoLink={infoLink}
            deliveryTypesDisabled={deliveryTypesDisabled}
            deliveryTypes={deliveryTypes}
            isBoxArticle={isBoxArticle}
            montageParents={montageParents}
            montages={montages}
            boxLogisticInformation={boxLogisticInformation}
            isBoxSoldOut={isBoxSoldOut}
            setStorePickerModalState={setStorePickerModalState}
            isUpdatingCart={!isEcommerceReady}
        >
            <div
                ref={deliveryRef}
                className={cn(className)}
                {...getTagZoneIdFor("pdp-delivery")}
            >
                {children}
            </div>
        </DeliveryContextProvider>
    );
};

export { DeliveryBase };
